import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, SlidersHorizontal, X, Eye, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import ReportDateTabs from '../../components/reports/ReportDateTabs';
import useReportDateRange, { REPORT_TABS } from '../../hooks/useReportDateRange';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import { SUPPORT_SLA_OPTIONS } from '../../redux/featureSlice';
import { getTenantsReport, downloadTenantsReportExcel, getPlatformReportFilters } from '../../redux/platformReportsSlice';
import { canAccess } from '../../utils/platformPermissions';
import { unlessSystemId } from '../../utils/systemId';

// TENANT REPORT — every hospital on the platform created in the chosen range,
// with the size of each (doctors, patients, appointments, open tickets) and
// the plan value recorded for it. Same shape as the Patients Report next to it
// in the rail; reads GET /api/platform/reports/tenants (reports.view). Sorting
// and paging are server-side — the counts are aggregates, so the client never
// holds the full list.

const EMPTY_FILTERS = { status: '', tenantType: '', countryId: '', connectionMode: '' };
const SORTABLE = ['name', 'createdAt', 'doctors', 'patients', 'appointments', 'planValue'];

const SortIcon = ({ sort, column }) => {
    if (sort.by !== column) return <ArrowUpDown className="w-3 h-3 opacity-60" />;
    return sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
};

// Module scope on purpose: declared inside ReportsTenants it would be a new
// component type every render, so React would remount every <th> and the sort
// button would lose keyboard focus the moment it is pressed. Sortable columns
// render a real <button> so Tab / Enter / Space reach them; aria-sort stays on
// the <th>.
const Th = ({ column, children, align = 'left', sort, onSort }) => {
    const sortable = SORTABLE.includes(column);
    const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
    return (
        <th
            className={`px-4 py-3 text-${align} text-xs font-bold uppercase tracking-wider whitespace-nowrap`}
            aria-sort={sort.by === column ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
            data-testid={`reports-tenants-header-${column}`}
        >
            {sortable ? (
                <button
                    type="button"
                    onClick={() => onSort(column)}
                    // preflight resets text-transform on <button>, so the header typography is repeated here
                    className={`inline-flex items-center gap-1 w-full ${justify} uppercase tracking-wider font-bold cursor-pointer select-none rounded px-1 -mx-1 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70`}
                    data-testid={`reports-tenants-sort-${column}`}
                >
                    {children}
                    <SortIcon sort={sort} column={column} />
                </button>
            ) : (
                <span className={`inline-flex items-center gap-1 ${justify}`}>{children}</span>
            )}
        </th>
    );
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const inr = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0);
// planValue is null when the tenant was never priced (tenants.planPricing NULL) —
// show a dash, not ₹0, which would read as "free". Matches TenantManagement.jsx
// and the blank cell the Excel export writes.
const planValueText = (n) => (n == null ? '—' : inr(n));
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
// The option lists may come as plain strings or { value, label } objects.
const toOption = (o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value ?? o.id, label: o.label ?? o.name ?? String(o.value ?? o.id) });

const ReportsTenants = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // The eye opens /tenants/:id/view, which sits under tenants.view (App.jsx),
    // not this page's reports.view — hide it when the role would only bounce.
    const canOpenTenant = canAccess('tenants.view');
    const { tenants, tenantsTotalItems, tenantsLoading, tenantsDownloadLoading, filters: filterOptions } = useSelector((state) => state.platformReports);

    const range = useReportDateRange({ defaultTab: 'month' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 400);
    const [sort, setSort] = useState({ by: 'createdAt', dir: 'desc' });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    useEffect(() => {
        dispatch(getPlatformReportFilters());
    }, [dispatch]);

    const options = useMemo(() => ({
        statuses: (filterOptions?.statuses || []).map(toOption),
        tenantTypes: (filterOptions?.tenantTypes || []).map(toOption),
        connectionModes: (filterOptions?.connectionModes || []).map(toOption),
        countries: (filterOptions?.countries || []).map((c) => ({ countryId: c.countryId, countryName: c.name })),
    }), [filterOptions]);
    const labelOf = (list, value) => list.find((o) => o.value === value)?.label || String(unlessSystemId(value, '')).replace(/[_-]+/g, ' ');
    const countryLabel = (id) => options.countries.find((c) => c.countryId === id)?.countryName || '';
    const slaLabel = (level) => {
        if (!level) return t('reportsTenants.sla_platform_default', 'Platform default');
        const opt = SUPPORT_SLA_OPTIONS.find((o) => o.value === level);
        return opt ? t(opt.labelKey, opt.labelFallback) : level;
    };

    const queryParams = (r) => ({
        ...r,
        search: debouncedSearch,
        status: filters.status,
        tenantType: filters.tenantType,
        countryId: filters.countryId,
        connectionMode: filters.connectionMode,
        sortBy: sort.by,
        sortDir: sort.dir,
    });

    const fetchTenants = async (page = 1, limitOverride) => {
        const r = range.resolveRange();
        if (!r) return;
        const result = await dispatch(getTenantsReport({ ...queryParams(r), page, limit: limitOverride || itemsPerPage }));
        if (getTenantsReport.rejected.match(result)) {
            toast.error(result.payload?.error || t('reportsTenants.load_failed', 'Failed to load the tenant report.'));
        }
    };

    // Any change to the question resets to page 1.
    useEffect(() => {
        setCurrentPage(1);
        fetchTenants(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.activeTab, range.startDate, range.endDate, filters, debouncedSearch, sort]);

    useEffect(() => {
        if (isFilterOpen) setTempFilters({ ...filters });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(event.target)
            ) {
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    const handlePageChange = (page, newLimit) => {
        setCurrentPage(page);
        if (newLimit && newLimit !== itemsPerPage) setItemsPerPage(newLimit);
        fetchTenants(page, newLimit || itemsPerPage);
    };

    const toggleSort = (column) => {
        if (!SORTABLE.includes(column)) return;
        setSort((prev) => (prev.by === column
            ? { by: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { by: column, dir: column === 'name' ? 'asc' : 'desc' }));
    };

    const handleDownload = async () => {
        const r = range.resolveRange({ warn: true });
        if (!r) return;
        try {
            const resultAction = await dispatch(downloadTenantsReportExcel(queryParams(r)));
            if (downloadTenantsReportExcel.fulfilled.match(resultAction)) {
                const blob = resultAction.payload;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `tenants-report-${r.from}-to-${r.to}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t('reportsTenants.excel_download_success', 'Excel downloaded successfully.'));
            } else {
                toast.warning(t('reportsTenants.excel_download_failed', 'Failed to download Excel.'));
            }
        } catch {
            toast.warning(t('reportsTenants.excel_download_failed', 'Failed to download Excel.'));
        }
    };

    const displayTenants = range.ready ? tenants : [];
    const totalItems = range.ready ? tenantsTotalItems : 0;
    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const activeFilterEntries = Object.entries(filters).filter(([, v]) => v);

    const filterLabels = {
        status: t('reportsTenants.filter_status', 'Status'),
        tenantType: t('reportsTenants.filter_tenant_type', 'Tenant type'),
        countryId: t('reportsTenants.filter_country', 'Country'),
        connectionMode: t('reportsTenants.filter_connection_mode', 'Connection mode'),
    };
    const filterValueLabel = (key, value) => {
        if (key === 'status') return labelOf(options.statuses, value);
        if (key === 'tenantType') return labelOf(options.tenantTypes, value);
        if (key === 'connectionMode') return labelOf(options.connectionModes, value);
        return countryLabel(value);
    };

    const StatusPill = ({ status }) => {
        const active = status === 'active';
        return (
            <span className={`inline-flex items-center text-[11px] font-bold border rounded-full px-2.5 py-0.5 ${active ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                {labelOf(options.statuses, status) || (active ? t('reportsTenants.active', 'Active') : t('reportsTenants.inactive', 'Inactive'))}
            </span>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-tenants-container"
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-tenants-header">
                <div>
                    <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-tenants-title">
                        {t('reportsTenants.title', 'Tenant Report')}
                    </h3>
                    <p className="mt-1 text-xs text-gray-400" data-testid="reports-tenants-subtitle">
                        {t('reportsTenants.platform_wide', 'All tenants on the platform — every hospital, clinic and solo practice')}
                    </p>
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    disabled={tenantsDownloadLoading || displayTenants.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    data-testid="reports-tenants-download-button"
                >
                    <Download className="w-4 h-4" />
                    {tenantsDownloadLoading ? t('reportsTenants.downloading', 'Downloading...') : t('reportsTenants.download_excel', 'Download Excel')}
                </motion.button>
            </div>

            {/* Tabs, search & filter */}
            <div className="mb-5">
                <ReportDateTabs range={range} tabs={REPORT_TABS} layoutId="activeTenantReportTab" testIdPrefix="reports-tenants">
                    <div className="relative w-full sm:w-64" data-testid="reports-tenants-search-container">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('reportsTenants.search_placeholder', 'Search name / id / contact…')}
                            maxLength={50}
                            className="w-full pl-9 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                            data-testid="reports-tenants-search-input"
                        />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 cursor-pointer" data-testid="reports-tenants-search-clear">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="relative w-full sm:w-auto" data-testid="reports-tenants-filter-container">
                        <button
                            ref={filterButtonRef}
                            type="button"
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsCommon.filter', 'Filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-tenants-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => { setTempFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setIsFilterOpen(false); }}
                                    onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                                    clearLabel={t('reportsCommon.filter_clear', 'Clear')}
                                    applyLabel={t('reportsCommon.filter_apply', 'Apply')}
                                >
                                    <FilterSection title={filterLabels.status} isActive={Boolean(tempFilters.status)}>
                                        <FilterOptionList value={tempFilters.status} onChange={(val) => setTempFilters({ ...tempFilters, status: val })} options={options.statuses} />
                                    </FilterSection>
                                    <FilterSection title={filterLabels.tenantType} isActive={Boolean(tempFilters.tenantType)}>
                                        <FilterOptionList value={tempFilters.tenantType} onChange={(val) => setTempFilters({ ...tempFilters, tenantType: val })} options={options.tenantTypes} />
                                    </FilterSection>
                                    <FilterSection title={filterLabels.connectionMode} isActive={Boolean(tempFilters.connectionMode)}>
                                        <FilterOptionList value={tempFilters.connectionMode} onChange={(val) => setTempFilters({ ...tempFilters, connectionMode: val })} options={options.connectionModes} />
                                    </FilterSection>
                                    <FilterSection title={filterLabels.countryId} isActive={Boolean(tempFilters.countryId)}>
                                        <FilterCountryList
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={options.countries}
                                            searchPlaceholder={t('reportsCommon.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                </ReportDateTabs>
            </div>

            {/* Applied filters */}
            {activeFilterEntries.length > 0 && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-tenants-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">{t('reportsCommon.applied_filters', 'Applied Filters')}:</span>
                        {activeFilterEntries.map(([key, value]) => (
                            <div key={key} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-tenants-applied-chip-${key}`}>
                                <span className="text-xs text-gray-600"><span className="font-semibold">{filterLabels[key]}:</span> {filterValueLabel(key, value)}</span>
                                <button type="button" onClick={() => setFilters({ ...filters, [key]: '' })} className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => { setFilters(EMPTY_FILTERS); setTempFilters(EMPTY_FILTERS); }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-sm cursor-pointer"
                            data-testid="reports-tenants-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('reportsCommon.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {tenantsLoading && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-tenants-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('reportsCommon.loading', 'Loading...')}</span>
                </div>
            )}

            {/* Desktop table */}
            {!tenantsLoading && (
                <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200" data-testid="reports-tenants-table-container">
                    <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="reports-tenants-table">
                        <thead>
                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                <Th sort={sort} onSort={toggleSort} column="serial">{t('reportsTenants.serial_no', 'Serial No.')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="name">{t('reportsTenants.tenant', 'Tenant')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="type">{t('reportsTenants.type', 'Type')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="status">{t('reportsTenants.status', 'Status')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="mode">{t('reportsTenants.mode', 'Mode')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="country">{t('reportsTenants.country', 'Country')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="contact">{t('reportsTenants.contact', 'Contact')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="doctors" align="right">{t('reportsTenants.doctors', 'Doctors')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="patients" align="right">{t('reportsTenants.patients', 'Patients')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="appointments" align="right">{t('reportsTenants.appointments', 'Appointments')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="openTickets" align="right">{t('reportsTenants.open_tickets', 'Open tickets')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="planValue" align="right">{t('reportsTenants.plan_value', 'Plan value')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="supportLevel">{t('reportsTenants.support_sla', 'Support SLA')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="createdAt">{t('reportsTenants.created_at', 'Created')}</Th>
                                <Th sort={sort} onSort={toggleSort} column="action" align="center">{t('reportsTenants.action', 'Action')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayTenants.length === 0 ? (
                                <tr data-testid="reports-tenants-empty-row">
                                    <td colSpan="15" className="p-8 text-center text-gray-500">{t('reportsCommon.no_data', 'No data available.')}</td>
                                </tr>
                            ) : (
                                displayTenants.map((tenant, index) => (
                                    <tr
                                        key={tenant.tenantId || index}
                                        className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group"
                                        data-testid={`reports-tenants-row-${index}`}
                                    >
                                        <td className="px-4 py-4 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                        <td className="px-4 py-4 text-xs">
                                            <p className="font-medium text-gray-800 whitespace-nowrap">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</p>
                                            {tenant.subdomain && <p className="font-mono text-[10px] text-gray-400">{tenant.subdomain}</p>}
                                        </td>
                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">{tenant.tenantTypeName || labelOf(options.tenantTypes, tenant.tenantType) || '-'}</td>
                                        <td className="px-4 py-4 text-xs"><StatusPill status={tenant.status} /></td>
                                        <td className="px-4 py-4 text-xs text-gray-600 capitalize whitespace-nowrap">{labelOf(options.connectionModes, tenant.connectionMode) || '-'}</td>
                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">{tenant.countryName || countryLabel(tenant.countryId) || '-'}</td>
                                        <td className="px-4 py-4 text-xs text-gray-600">
                                            <p>{tenant.contactEmail || '-'}</p>
                                            <p className="text-gray-400">{tenant.contactPhone || ''}</p>
                                        </td>
                                        <td className="px-4 py-4 text-xs text-right text-gray-700 tabular-nums">{fmtNum(tenant.doctors)}</td>
                                        <td className="px-4 py-4 text-xs text-right text-gray-700 tabular-nums">{fmtNum(tenant.patients)}</td>
                                        <td className="px-4 py-4 text-xs text-right text-gray-700 tabular-nums">{fmtNum(tenant.appointments)}</td>
                                        <td className="px-4 py-4 text-xs text-right tabular-nums">
                                            <span className={(Number(tenant.openTickets) || 0) > 0 ? 'font-semibold text-amber-700' : 'text-gray-500'}>{fmtNum(tenant.openTickets)}</span>
                                        </td>
                                        <td className="px-4 py-4 text-xs text-right font-semibold text-gray-800 tabular-nums whitespace-nowrap">{planValueText(tenant.planValue)}</td>
                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">{slaLabel(tenant.supportLevel)}</td>
                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">{formatDate(tenant.createdAt)}</td>
                                        <td className="px-4 py-4 text-center">
                                            {canOpenTenant && (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/tenants/${tenant.tenantId}/view`)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden inline-flex items-center justify-center"
                                                aria-label={t('reportsTenants.view', 'View tenant')}
                                                data-testid={`reports-tenants-view-button-${index}`}
                                            >
                                                <Eye className="h-4 w-4 relative z-10" />
                                            </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile cards */}
            {!tenantsLoading && (
                <div className="block sm:hidden space-y-4" data-testid="reports-tenants-mobile-container">
                    {displayTenants.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">{t('reportsCommon.no_data', 'No data available.')}</div>
                    ) : (
                        displayTenants.map((tenant, index) => (
                            <div key={tenant.tenantId || index} className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid={`reports-tenants-mobile-card-${index}`}>
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-[#4ABDBD]">{t('reportsTenants.serial_no', 'Serial No.')}: {indexOfFirst + index + 1}</span>
                                    {canOpenTenant && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/tenants/${tenant.tenantId}/view`)}
                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md cursor-pointer transition-all"
                                        aria-label={t('reportsTenants.view', 'View tenant')}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>
                                    )}
                                </div>
                                <div className="text-sm font-medium mb-1">{tenant.name || t('moc.hr_detail_hospital', 'Hospital')}</div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    <StatusPill status={tenant.status} />
                                    <span className="inline-flex items-center text-[11px] font-bold border rounded-full px-2.5 py-0.5 bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]/60">{tenant.tenantTypeName || labelOf(options.tenantTypes, tenant.tenantType)}</span>
                                </div>
                                {tenant.subdomain && <div className="text-xs text-gray-600 font-mono">{tenant.subdomain}</div>}
                                <div className="text-xs text-gray-600">{t('reportsTenants.country', 'Country')}: {tenant.countryName || '-'}</div>
                                <div className="text-xs text-gray-600">{t('reportsTenants.contact', 'Contact')}: {tenant.contactEmail || tenant.contactPhone || '-'}</div>
                                <div className="text-xs text-gray-600">{t('reportsTenants.doctors', 'Doctors')}: {fmtNum(tenant.doctors)} · {t('reportsTenants.patients', 'Patients')}: {fmtNum(tenant.patients)} · {t('reportsTenants.appointments', 'Appointments')}: {fmtNum(tenant.appointments)}</div>
                                <div className="text-xs text-gray-600">{t('reportsTenants.open_tickets', 'Open tickets')}: {fmtNum(tenant.openTickets)} · {t('reportsTenants.plan_value', 'Plan value')}: {planValueText(tenant.planValue)}</div>
                                <div className="text-xs text-gray-600">{t('reportsTenants.created_at', 'Created')}: {formatDate(tenant.createdAt)}</div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {!tenantsLoading && totalItems > 0 && (
                <div data-testid="reports-tenants-pagination-container">
                    <Pagination
                        totalItems={totalItems}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        custombutton={displayTenants.length === 0 ? 'disable' : ''}
                    />
                </div>
            )}
        </motion.div>
    );
};

export default ReportsTenants;
